import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {TrendingUp, Star, Sparkles, Loader2, Store, ArrowRight} from 'lucide-react'
import CouponCard from '../components/CouponCard'
import Newsletter from '../components/Newsletter'
import PersonalizedRecommendations from '../components/PersonalizedRecommendations'
import { lumi } from '../lib/lumi'

interface Coupon {
  _id: string
  title: string
  description: string
  code?: string
  discount: string
  expiryDate?: string
  verified?: boolean
  affiliateLink: string
  featured?: boolean
  brand: string
  category?: string
  active?: boolean
  clicks?: number
}

export default function HomePage() {
  const [featuredCoupon, setFeaturedCoupon] = useState<Coupon | null>(null)
  const [trendingCoupons, setTrendingCoupons] = useState<Coupon[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    document.title = 'Glen Ivy Coupons at Costco & Exclusive Promo Codes | GlenCode'
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Find verified Glen Ivy promo codes, Costco deals, and exclusive discount codes for Glen Ivy Hot Springs, Glen\'s Army Navy, and more. Save big today!'
      )
    }

    // Fetch coupons from database
    fetchCoupons()
  }, [])

  const fetchCoupons = async () => {
    try {
      setLoading(true)
      
      // Fetch featured coupon
      const featuredResult = await lumi.entities.coupons.list({
        filter: { featured: true, active: true },
        sort: { clicks: -1 },
        limit: 1
      })
      
      if (featuredResult.list.length > 0) {
        setFeaturedCoupon(featuredResult.list[0])
      }

      // Fetch trending coupons
      const trendingResult = await lumi.entities.coupons.list({
        filter: { featured: false, active: true },
        sort: { clicks: -1 },
        limit: 6
      })
      
      setTrendingCoupons(trendingResult.list)
    } catch (error) {
      console.error('Failed to fetch coupons:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 via-blue-50 to-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Star className="w-6 h-6 text-yellow-500 fill-yellow-500" />
              <span className="text-green-700 font-semibold">Trusted by 10,000+ Deal Hunters</span>
              <Star className="w-6 h-6 text-yellow-500 fill-yellow-500" />
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              The Best Glen Ivy Coupons at Costco & Online
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8">
              Verified promo codes for Glen Ivy Hot Springs, Glen's Army Navy, and more.
            </p>
            
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-700">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                <Sparkles className="w-4 h-4 text-green-600" />
                <span>Updated Daily</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                <TrendingUp className="w-4 h-4 text-blue-600" />
                <span>100% Verified Codes</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Deal Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <Star className="w-8 h-8 text-green-600 fill-green-600" />
            <h2 className="text-3xl font-bold text-gray-900">Featured Deal</h2>
          </div>
          
          <div className="max-w-2xl">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
              </div>
            ) : featuredCoupon ? (
              <CouponCard
                title={featuredCoupon.title}
                description={featuredCoupon.description}
                discount={featuredCoupon.discount}
                code={featuredCoupon.code}
                expiryDate={featuredCoupon.expiryDate}
                verified={featuredCoupon.verified}
                affiliateLink={featuredCoupon.affiliateLink}
                featured={true}
                couponId={featuredCoupon._id}
                onReveal={fetchCoupons}
              />
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p>No featured deals available at the moment. Check back soon!</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Personalized Recommendations Section */}
      <PersonalizedRecommendations />

      {/* Top Trending Codes Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Top Trending Glen Codes</h2>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
            </div>
          ) : trendingCoupons.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trendingCoupons.map((coupon) => (
                <CouponCard
                  key={coupon._id}
                  title={coupon.title}
                  description={coupon.description}
                  discount={coupon.discount}
                  code={coupon.code}
                  expiryDate={coupon.expiryDate}
                  verified={coupon.verified}
                  affiliateLink={coupon.affiliateLink}
                  couponId={coupon._id}
                  onReveal={fetchCoupons}
                  showRatings={true}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p>No coupons available at the moment. Check back soon!</p>
            </div>
          )}

          <div className="text-center mt-12">
            <Link
              to="/glen-ivy-guide"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-lg hover:from-green-700 hover:to-blue-700 transition-all shadow-md hover:shadow-lg text-lg"
            >
              View Complete Glen Ivy Guide
              <TrendingUp className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Blog Preview Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Latest Money-Saving Tips</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/blog/glen-ivy-costco-guide-2025"
              className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="h-48 bg-gradient-to-br from-green-400 to-blue-500"></div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  How to find Glen Ivy Coupons at Costco (2025 Guide)
                </h3>
                <p className="text-gray-600">
                  Discover insider tips to maximize your savings at Costco...
                </p>
              </div>
            </Link>
            
            <Link
              to="/blog/glens-army-navy-free-shipping"
              className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="h-48 bg-gradient-to-br from-blue-400 to-purple-500"></div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Glen's Army Navy Store: How to score free shipping
                </h3>
                <p className="text-gray-600">
                  Learn the secret tricks to getting free shipping every time...
                </p>
              </div>
            </Link>
            
            <Link
              to="/blog/hidden-promo-codes-glencode"
              className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="h-48 bg-gradient-to-br from-purple-400 to-pink-500"></div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Hidden Promo Codes: The Glencode Official List
                </h3>
                <p className="text-gray-600">
                  Exclusive codes you won't find anywhere else...
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Explore Glen Brands Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Store className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Explore 13+ Glen Brands & Locations
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From luxury spa retreats to whisky distilleries, entertainment venues to outdoor adventures
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { name: 'Glen Ivy Hot Springs', category: 'Spa & Wellness' },
              { name: 'Glenwood Springs', category: 'Hot Springs' },
              { name: 'Glen Ellen', category: 'Wine & Dining' },
              { name: 'Glenfiddich', category: 'Spirits' },
              { name: 'Glen Canyon', category: 'National Parks' },
              { name: 'Glen Helen', category: 'Entertainment' },
              { name: 'Gleneagles', category: 'Golf Resorts' },
              { name: 'The Glenlivet', category: 'Distilleries' }
            ].map((brand) => (
              <div key={brand.name} className="bg-white p-4 rounded-lg border-2 border-gray-200 hover:border-green-500 transition shadow-sm">
                <Store className="w-6 h-6 text-green-600 mb-2" />
                <p className="font-semibold text-gray-900 text-sm">{brand.name}</p>
                <p className="text-xs text-gray-500">{brand.category}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/all-brands"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-lg hover:from-green-700 hover:to-blue-700 transition-all shadow-md hover:shadow-lg text-lg"
            >
              Discover All Glen Brands
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Newsletter />
        </div>
      </section>
    </div>
  )
}
